import classNames from 'classnames';
import { compareDesc } from 'date-fns';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Switcher } from '@sorare/core/src/atoms/navigation/Switcher';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { MarketHero } from '#src/components/market-home/MarketHero/index.tsx';
import { MarketHomeHero } from '#src/components/market-home/MarketHomeHero/index.tsx';

type PromotionalEvent = ReturnType<
  typeof useConfigContext
>['promotionalEvents'][number];

type Props = {
  rarity: Rarity;
  sport: Sport;
  topBar?: ReactNode;
};

const SLIDE_DURATION = 10000;

const root = css`
  position: relative;
  isolation: isolate;
  margin: 0 calc(var(--container-padding, 0px) * -1);
  overflow: hidden;
  display: grid;
  grid-template-areas: 'slide';
`;

/* Every slide shares one grid cell, so the carousel is as tall as its tallest
   slide and never resizes while switching. */
const slide = css`
  grid-area: slide;
  display: grid;
  transition:
    transform 0.4s ease-in-out,
    opacity 0.4s ease-in-out;

  &.before {
    transform: translateX(-100%);
  }
  &.after {
    transform: translateX(100%);
  }
  &.before,
  &.after {
    opacity: 0;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 0.4s ease-in-out;

    &.before,
    &.after {
      transform: none;
    }
  }
`;

const topBarSlot = css`
  position: absolute;
  top: var(--double-unit);
  left: 0;
  right: 0;
  padding: 0 var(--container-padding, var(--double-unit));
`;

const switcherSlot = css`
  position: absolute;
  bottom: var(--double-unit);
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;

  @media ${tabletAndAbove} {
    justify-content: flex-start;
    padding: 0 var(--container-padding, var(--double-unit));
  }
`;

const getShopNowUrl = (promotionalEvent: PromotionalEvent, sport: Sport) => {
  const route =
    ('hasInstantBuy' in promotionalEvent && promotionalEvent.hasInstantBuy) ||
    isType(promotionalEvent, 'CartPurchaseRewardPromotionalEvent')
      ? ANY_SPORT_MARKET_SHOP_INSTANT_BUY
      : ANY_SPORT_MARKET_SHOP_AUCTIONS;

  return `${generateSportPath(route, { sport })}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${promotionalEvent.slug}#market`;
};

export const MarketHeroCarousel = ({ rarity, sport, topBar }: Props) => {
  const { promotionalEvents } = useConfigContext();
  const [activeIndex, setActiveIndex] = useState(0);

  const promotionalEventsWithBackground = useMemo(
    () =>
      promotionalEvents
        .flatMap(promotionalEvent =>
          promotionalEvent.backgroundPictureUrl
            ? [
                {
                  ...promotionalEvent,
                  backgroundPictureUrl: promotionalEvent.backgroundPictureUrl,
                },
              ]
            : []
        )
        .sort((a, b) => compareDesc(a.startDate, b.startDate)),
    [promotionalEvents]
  );

  const promoSlides = promotionalEventsWithBackground.map(promotionalEvent => ({
    key: promotionalEvent.slug,
    hero: (
      <MarketHero
        backgroundUrl={promotionalEvent.backgroundPictureUrl}
        countdown={promotionalEvent.endDate}
        icon={
          promotionalEvent.logoUrl ? (
            <img src={promotionalEvent.logoUrl} alt={promotionalEvent.title} />
          ) : undefined
        }
        subtitle={promotionalEvent.description}
        primaryCta={{
          label: <FormattedMessage defaultMessage="Shop now" />,
          to: getShopNowUrl(promotionalEvent, sport),
        }}
      />
    ),
  }));

  const slides = promoSlides.length
    ? promoSlides
    : [
        {
          key: 'market-home',
          hero: <MarketHomeHero rarity={rarity} sport={sport} />,
        },
      ];
  const slideCount = slides.length;

  useEffect(() => {
    if (slideCount <= 1) {
      return undefined;
    }
    const timeout = setTimeout(
      () => setActiveIndex(index => (index + 1) % slideCount),
      SLIDE_DURATION
    );
    return () => clearTimeout(timeout);
  }, [activeIndex, slideCount]);

  return (
    <div className={root}>
      {slides.map(({ key, hero }, index) => (
        <div
          key={key}
          className={classNames(slide, {
            before: index < activeIndex,
            after: index > activeIndex,
          })}
          aria-hidden={index !== activeIndex}
        >
          {hero}
        </div>
      ))}
      {!!topBar && <div className={topBarSlot}>{topBar}</div>}
      <div className={switcherSlot}>
        <Switcher
          numberOfItems={slideCount}
          activeIndex={activeIndex}
          onSelect={setActiveIndex}
        />
      </div>
    </div>
  );
};
